import React, { useState } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import {
  Building2,
  Plus,
  Settings,
  Users,
  Crown,
  Shield,
  User,
  Trash2,
  Edit,
  LogOut,
  Brain,
  Calendar
} from 'lucide-react';

const WorkspaceManagement = () => {
  const {
    currentWorkspace,
    workspaces,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
    switchWorkspace,
    removeMember,
    updateMemberRole,
    leaveWorkspace,
    getWorkspaceMembers
  } = useWorkspace();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingWorkspace, setEditingWorkspace] = useState(null);

  const [newWorkspace, setNewWorkspace] = useState({
    name: '',
    description: ''
  });

  const [editWorkspace, setEditWorkspace] = useState({
    name: '',
    description: ''
  });

  const handleCreateWorkspace = async (e) => {
    e.preventDefault();
    if (!newWorkspace.name.trim()) return;

    setLoading(true);
    const result = await createWorkspace(newWorkspace.name, newWorkspace.description);
    if (result.success) {
      setNewWorkspace({ name: '', description: '' });
      setShowCreateForm(false);
    }
    setLoading(false);
  };

  const handleEditWorkspace = async (e) => {
    e.preventDefault();
    if (!editWorkspace.name.trim() || !editingWorkspace) return;

    setLoading(true);
    const result = await updateWorkspace(editingWorkspace.id, editWorkspace);
    if (result.success) {
      setEditWorkspace({ name: '', description: '' });
      setEditingWorkspace(null);
      setShowEditForm(false);
    }
    setLoading(false);
  };

  const startEditWorkspace = (workspace) => {
    setEditingWorkspace(workspace);
    setEditWorkspace({
      name: workspace.name,
      description: workspace.description || ''
    });
    setShowEditForm(true);
  };

  const cancelEdit = () => {
    setEditingWorkspace(null);
    setEditWorkspace({ name: '', description: '' });
    setShowEditForm(false);
  };

  const loadMembers = async () => {
    setLoading(true);
    const result = await getWorkspaceMembers(currentWorkspace.id);
    if (result.success) {
      setMembers(result.members);
      setShowMembers(true);
    }
    setLoading(false);
  };

  const handleRemoveMember = async (userId) => {
    if (window.confirm('Are you sure you want to remove this member?')) {
      setLoading(true);
      await removeMember(currentWorkspace.id, userId);
      await loadMembers(); // Reload members
      setLoading(false);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    setLoading(true);
    await updateMemberRole(currentWorkspace.id, userId, newRole);
    await loadMembers(); // Reload members
    setLoading(false);
  };

  const handleLeaveWorkspace = async (workspaceId) => {
    if (window.confirm('Are you sure you want to leave this workspace?')) {
      setLoading(true);
      await leaveWorkspace(workspaceId);
      setLoading(false);
    }
  };

  const handleDeleteWorkspace = async (workspaceId) => {
    if (window.confirm('Are you sure you want to delete this workspace? This action cannot be undone.')) {
      setLoading(true);
      await deleteWorkspace(workspaceId);
      setLoading(false);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner':
        return <Crown className="w-4 h-4 text-yellow-500" />;
      case 'admin':
        return <Shield className="w-4 h-4 text-blue-500" />;
      default:
        return <User className="w-4 h-4 text-gray-500" />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'owner':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'admin':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  if (!currentWorkspace) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No workspace selected</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Workspace Management</h1>
          <p className="text-gray-600">Manage your workspaces and team members.</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
        >
          <Plus className="w-4 h-4" />
          <span>Create Workspace</span>
        </button>
      </div>

      {/* Workspaces Table */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Building2 className="w-5 h-5 mr-2 text-blue-600" />
            {currentWorkspace?.id === 1 ? 'All Workspaces (Admin View)' : 'Your Workspaces'}
          </h2>
        </div>

        {workspaces.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">No workspaces found</p>
            <p className="text-sm text-gray-400">Create your first workspace to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Workspace
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  {currentWorkspace?.id === 1 && (
                    <>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created By
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created Date
                      </th>
                    </>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Users
                  </th>
                  {currentWorkspace?.id === 1 && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      AI Usage
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {currentWorkspace?.id === 1 ? 'Your Role' : 'Your Role'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {workspaces.map((workspace) => (
                  <tr key={workspace.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center mr-3">
                          <Building2 className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{workspace.name}</div>
                          <div className="text-sm text-gray-500">ID: {workspace.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate">
                        {workspace.description || 'No description'}
                      </div>
                    </td>
                    {currentWorkspace?.id === 1 && (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <User className="w-4 h-4 text-gray-400 mr-1" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">{workspace.creator_name || 'Unknown'}</div>
                              <div className="text-xs text-gray-500">{workspace.creator_email || ''}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 text-gray-400 mr-1" />
                            <span className="text-sm text-gray-900">
                              {workspace.created_at ? new Date(workspace.created_at).toLocaleDateString() : 'N/A'}
                            </span>
                          </div>
                        </td>
                      </>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Users className="w-4 h-4 text-gray-400 mr-1" />
                        <span className="text-sm text-gray-900">{workspace.total_users || workspace.member_count || 0}</span>
                      </div>
                    </td>
                    {currentWorkspace?.id === 1 && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col">
                          <div className="flex items-center mb-1">
                            <Brain className="w-4 h-4 text-purple-400 mr-1" />
                            <span className="text-sm font-medium text-gray-900">{workspace.ai_uses || 0} uses</span>
                          </div>
                          <div className="text-xs text-gray-500">
                            {workspace.ai_tokens ? `${parseInt(workspace.ai_tokens).toLocaleString()} tokens` : '0 tokens'}
                          </div>
                        </div>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      {workspace.user_role ? (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(workspace.user_role)}`}>
                          {getRoleIcon(workspace.user_role)}
                          <span className="ml-1 capitalize">{workspace.user_role}</span>
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">Not a member</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        {workspace.user_role && (
                          <button
                            onClick={() => switchWorkspace(workspace)}
                            className="text-blue-600 hover:text-blue-900 p-1"
                            title="Switch to this workspace"
                          >
                            <Settings className="w-4 h-4" />
                          </button>
                        )}
                        {workspace.user_role && ['owner', 'admin'].includes(workspace.user_role) && (
                          <button
                            onClick={() => startEditWorkspace(workspace)}
                            className="text-gray-600 hover:text-gray-900 p-1"
                            title="Edit workspace"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        )}
                        {workspace.id !== 1 && workspace.user_role === 'owner' && (
                          <button
                            onClick={() => handleDeleteWorkspace(workspace.id)}
                            className="text-red-600 hover:text-red-900 p-1"
                            title="Delete workspace"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                        {workspace.id !== 1 && workspace.user_role && workspace.user_role !== 'owner' && (
                          <button
                            onClick={() => handleLeaveWorkspace(workspace.id)}
                            className="text-orange-600 hover:text-orange-900 p-1"
                            title="Leave workspace"
                          >
                            <LogOut className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Current Workspace Info */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{currentWorkspace.name}</h2>
              <p className="text-gray-600">{currentWorkspace.description || 'No description'}</p>
              <div className="flex items-center space-x-2 mt-1">
                {getRoleIcon(currentWorkspace.user_role)}
                <span className="text-sm text-gray-500 capitalize">
                  {currentWorkspace.user_role}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={loadMembers}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
            >
              <Users className="w-4 h-4" />
              <span>View Members</span>
            </button>
            {currentWorkspace.user_role === 'owner' && (
              <button
                onClick={handleDeleteWorkspace}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors duration-200"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete</span>
              </button>
            )}
            {currentWorkspace.user_role !== 'owner' && (
              <button
                onClick={handleLeaveWorkspace}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors duration-200"
              >
                <LogOut className="w-4 h-4" />
                <span>Leave</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Create New Workspace */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Plus className="w-5 h-5 mr-2 text-green-600" />
              Create Workspace
            </h3>
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="text-green-600 hover:text-green-700 font-medium"
            >
              {showCreateForm ? 'Cancel' : 'Create'}
            </button>
          </div>

          {showCreateForm ? (
            <form onSubmit={handleCreateWorkspace} className="space-y-4">
              <div>
                <input
                  type="text"
                  placeholder="Workspace name"
                  value={newWorkspace.name}
                  onChange={(e) => setNewWorkspace({ ...newWorkspace, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <input
                  type="text"
                  placeholder="Description (optional)"
                  value={newWorkspace.description}
                  onChange={(e) => setNewWorkspace({ ...newWorkspace, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Workspace'}
              </button>
            </form>
          ) : (
            <p className="text-gray-600 text-sm">
              Create a new workspace for different projects or teams.
            </p>
          )}
        </div>
      </div>

      {/* Members List */}
      {showMembers && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2 text-blue-600" />
            Workspace Members
          </h3>

          <div className="space-y-3">
            {members.map((member) => (
              <div key={member.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-gray-500" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{member.name}</p>
                    <p className="text-sm text-gray-500">{member.email}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getRoleColor(member.role)}`}>
                    {getRoleIcon(member.role)}
                    <span className="ml-1 capitalize">{member.role}</span>
                  </span>

                  {currentWorkspace.user_role === 'owner' && member.role !== 'owner' && (
                    <div className="flex items-center space-x-2">
                      <select
                        value={member.role}
                        onChange={(e) => handleUpdateRole(member.user_id, e.target.value)}
                        className="text-sm border border-gray-300 rounded px-2 py-1"
                      >
                        <option value="member">Member</option>
                        <option value="admin">Admin</option>
                      </select>
                      <button
                        onClick={() => handleRemoveMember(member.user_id)}
                        className="text-red-600 hover:text-red-700 p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create Workspace Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Workspace</h3>
            <form onSubmit={handleCreateWorkspace} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Workspace Name
                </label>
                <input
                  type="text"
                  placeholder="Enter workspace name"
                  value={newWorkspace.name}
                  onChange={(e) => setNewWorkspace({ ...newWorkspace, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  placeholder="Enter workspace description"
                  value={newWorkspace.description}
                  onChange={(e) => setNewWorkspace({ ...newWorkspace, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                />
              </div>
              <div className="flex items-center space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
                >
                  {loading ? 'Creating...' : 'Create Workspace'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors duration-200"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Workspace Modal */}
      {showEditForm && editingWorkspace && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Workspace</h3>
            <form onSubmit={handleEditWorkspace} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Workspace Name
                </label>
                <input
                  type="text"
                  placeholder="Enter workspace name"
                  value={editWorkspace.name}
                  onChange={(e) => setEditWorkspace({ ...editWorkspace, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  placeholder="Enter workspace description"
                  value={editWorkspace.description}
                  onChange={(e) => setEditWorkspace({ ...editWorkspace, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                />
              </div>
              <div className="flex items-center space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
                >
                  {loading ? 'Updating...' : 'Update Workspace'}
                </button>
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors duration-200"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkspaceManagement;
